/**
 * credits-format.ts — Push 5-post-smoke-4 (2026-07-11) formatting helpers
 * for the credit ↔ dollar bridge shown on CreditsPoolCard + related surfaces.
 *
 * Adjudication 10 (Push 5-post-smoke-4): "Credits primary; $ contextual.
 * $ leads on money-going-OUT (spent, cap). Credits lead on stuff-you-have
 * (balance)." Helpers here render both sides so callsites can pick.
 *
 * CREDITS_PER_USD is a placeholder constant pending the final pricing lock;
 * downstream surfaces should never render dollars without an "≈" prefix so
 * the approximation is visible to the user. When the final pricing lock
 * lands, one edit here updates every surface.
 */

/**
 * Placeholder rate — 50 credits per USD (0.02 USD per credit). Aligns with
 * the current pre-paid bundle pricing ($20 → 1000 credits base tier per
 * billing__product seed data). Update in one place when the final rate
 * lock ships.
 */
export const CREDITS_PER_USD = 50;

/**
 * Render an integer count of credits as a locale-formatted string.
 */
export function formatCreditsInt(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined) return '—';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return Math.round(n).toLocaleString();
}

/**
 * Render a dollar value formatted like Anthropic Console: `$127`, `$1,234`,
 * `$6.90`, `$10.97`.
 *
 * Sub-agent OOOOO (2026-07-31) — precision fix. Every dollar amount derived
 * from credits shows cents when there ARE cents, and drops the trailing
 * ".00" when the value is a whole dollar. That keeps balance / spend
 * surfaces accurate ($10.97 for 548.665744 credits, not $11) without
 * making integer-dollar surfaces (spend limits, bundle prices) noisy
 * with a redundant ".00".
 *
 * Rules:
 *   - Non-finite → em-dash
 *   - Zero → `$0`
 *   - Positive-but-below-one-cent → `<$0.01` (avoids misleading `$0.00`)
 *   - Enormous (>= $100k) → integer with commas, no cents
 *   - Whole-dollar → `$1,000` (no trailing `.00`)
 *   - Otherwise → `$X.YZ` (2 decimals, comma-separated thousands)
 */
export function formatDollars(usd: number): string {
  if (!Number.isFinite(usd)) return '—';
  if (usd === 0) return '$0';
  if (usd > 0 && usd < 0.005) return '<$0.01';
  if (usd < 0 && usd > -0.005) return '-<$0.01';

  // For huge values (unlikely in day-to-day workspace UI) drop cents for
  // legibility — a $127,543.90 spent this month reads better as $127,544.
  if (Math.abs(usd) >= 100_000) {
    return `$${Math.round(usd).toLocaleString('en-US')}`;
  }

  // Round to cents, then choose formatting based on whether the result
  // has a fractional part. This preserves the "spend limits render whole
  // dollars" rule while showing cents for balances derived from arbitrary
  // credit counts.
  const rounded = Math.round(usd * 100) / 100;
  const isInteger = Number.isInteger(rounded);
  return `$${rounded.toLocaleString('en-US', {
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Convenience: convert a credit count to an approximate USD label
 * (`≈ $6.90`). Always prefixed with `≈` so the approximation is visible.
 */
export function formatUsdFromCredits(credits: number): string {
  if (!Number.isFinite(credits) || credits <= 0) return '$0';
  const usd = credits / CREDITS_PER_USD;
  return `≈ ${formatDollars(usd)}`;
}

/**
 * Push 78 — admin surfaces render dollars only (per Ean directive:
 * "credits vs dollars display toggle only applies to end-user standard
 * app; admin surfaces = $ only, always"). This helper is the canonical
 * admin-side money formatter; every admin card / table / kebab uses it
 * instead of the "$X · N credits" dual-render pattern.
 *
 * Accepts a raw credit count (string or number, matching the wire shape
 * of `sys__account.token_balance_cached_credits` and friends). Returns
 * the dollar equivalent with the same `formatDollars` rounding rules
 * (whole dollars render as `$1,000`, fractional as `$10.97`, below one
 * cent as `<$0.01`, >= $100k as integer with commas). Null / undefined /
 * NaN renders as an em-dash so table cells never leak "NaN" text.
 */
export function formatAdminMoney(
  credits: string | number | null | undefined,
): string {
  if (credits === null || credits === undefined) return '—';
  const n = typeof credits === 'number' ? credits : Number(credits);
  if (!Number.isFinite(n)) return '—';
  return formatDollars(n / CREDITS_PER_USD);
}

/**
 * Parse a user-typed USD string ("6", "6.00", "$1,000") into an integer
 * credit count. Returns null when the input is blank or not a positive
 * number. Used by admin-side "Set spend limit" modals where the input
 * is dollars (Anthropic Console reference DNA) but the wire is credits.
 */
export function parseAdminDollarsToCredits(raw: string): number | null {
  const trimmed = raw.trim().replace(/^\$/, '').replace(/,/g, '');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * CREDITS_PER_USD);
}
